(function (Drupal, once) {
  'use strict';

  const BODY_COLLAPSED_CLASS = 'filter-collapsed';
  const REGION_HIDDEN_CLASS = 'is-filter-hidden';
  const TRANSITION_FALLBACK_MS = 500;
  const instances = new WeakMap();
  let currentExpanded = null;

  /**
   * Updates the global body state.
   *
   * @param {boolean} expanded
   *   Whether the filter panel is expanded.
   */
  function updateBodyState(expanded) {
    document.body.classList.toggle(
      BODY_COLLAPSED_CLASS,
      !expanded
    );
  }

  /**
   * Returns whether the toggle is currently expanded.
   *
   * @param {HTMLElement} toggle
   *   The filter toggle button.
   *
   * @return {boolean}
   *   True when expanded.
   */
  function isExpanded(toggle) {
    return toggle.getAttribute('aria-expanded') === 'true';
  }

  /**
   * Sets the accessible toggle state.
   *
   * @param {HTMLElement} toggle
   *   The filter toggle button.
   * @param {boolean} expanded
   *   Whether the filter panel is expanded.
   */
  function setExpanded(toggle, expanded) {
    currentExpanded = expanded;
    toggle.setAttribute('aria-expanded', String(expanded));
    const german = document.documentElement.lang.startsWith('de');
    toggle.setAttribute('aria-label', german
      ? (expanded ? 'Filter ausblenden' : 'Filter anzeigen')
      : (expanded ? Drupal.t('Hide filters') : Drupal.t('Show filters')));
    const controls = toggle.closest('.catalog-filter-controls');
    if (controls?.dataset.active === 'false'
      || getComputedStyle(controls).getPropertyValue('--catalog-filters-compact').trim() === '1') {
      controls.querySelector('.catalog-filter-action').setAttribute('aria-expanded', String(expanded));
    }
    if (Drupal.adocsCatalog.settings().rememberPanelState) {
      Drupal.adocsCatalog.store('expanded', expanded);
    }
    updateBodyState(expanded);
  }

  /**
   * Initializes one filter toggle.
   *
   * @param {HTMLElement} toggle
   *   The filter toggle button.
   * @param {HTMLElement} panel
   *   The controlled filter panel.
   */
  function initializeFilterToggle(toggle, panel) {
    const region = panel.closest('.region-filter');
    const catalog = Drupal.adocsCatalog;
    const controls = toggle.closest('.catalog-filter-controls');
    const action = controls.querySelector('.catalog-filter-action');
    const actionLabel = document.createElement('span');
    actionLabel.className = 'catalog-filter-action__label';
    const selection = document.createElement('span');
    selection.className = 'catalog-filter-action__selection';
    action.replaceChildren(actionLabel, selection);
    const isCompact = () => getComputedStyle(controls)
      .getPropertyValue('--catalog-filters-compact').trim() === '1';
    const listeners = new AbortController();
    const listenerOptions = { signal: listeners.signal };

    function updateAction() {
      const active = catalog.active(catalog.getState());
      const compact = isCompact();
      const state = catalog.getState();
      const labels = (catalog.settings().filters || []).flatMap((key) => {
        if (!state[key] || state[key] === 'All') return [];
        const link = catalog.getForm()?.querySelector(
          `.bef-links[data-name="${key}"] a[data-bef-value="${state[key]}"]`
        );
        return link ? [link.textContent.trim()] : [];
      });
      if (state.shuffle === '1') {
        labels.push(document.documentElement.lang.startsWith('de') ? 'Zufall' : Drupal.t('Shuffle'));
      }
      controls.dataset.active = String(active);
      actionLabel.textContent = compact
        ? controls.dataset.filterLabel + (labels.length ? ':' : '')
        : (active ? controls.dataset.resetLabel : controls.dataset.filterLabel);
      selection.textContent = labels.join(', ');
      selection.hidden = !compact || !labels.length;
      if (active && !compact) action.removeAttribute('aria-expanded');
      else action.setAttribute('aria-expanded', String(isExpanded(toggle)));
      toggle.tabIndex = active && !compact ? 0 : -1;
    }

    controls.addEventListener('adocs:catalog-state', updateAction, listenerOptions);
    window.addEventListener('resize', updateAction, listenerOptions);
    action.addEventListener('click', () => {
      if (!isCompact() && catalog.active(catalog.getState())) catalog.reset();
      else toggle.click();
    }, listenerOptions);
    updateAction();

    let transitionEndHandler = null;
    let transitionFallback = null;
    let animationFrame = null;

    /**
     * Removes an existing transition completion handler.
     */
    function clearTransitionHandler() {
      if (animationFrame !== null) {
        cancelAnimationFrame(animationFrame);
        animationFrame = null;
      }
      if (transitionEndHandler) {
        panel.removeEventListener(
          'transitionend',
          transitionEndHandler
        );

        transitionEndHandler = null;
      }

      if (transitionFallback) {
        window.clearTimeout(transitionFallback);
        transitionFallback = null;
      }
    }

    /**
     * Runs a callback after the max-height transition.
     *
     * A timeout is used as fallback in case transitionend does not fire,
     * for example when transitions are disabled or reduced.
     *
     * @param {Function} callback
     *   The callback to execute after the transition.
     */
    function afterTransition(callback) {
      clearTransitionHandler();

      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        callback();
        return;
      }

      let completed = false;

      const complete = () => {
        if (completed) {
          return;
        }

        completed = true;
        clearTransitionHandler();
        callback();
      };

      transitionEndHandler = (event) => {
        if (
          event.target !== panel ||
          event.propertyName !== 'max-height'
        ) {
          return;
        }

        complete();
      };

      panel.addEventListener(
        'transitionend',
        transitionEndHandler
      );

      transitionFallback = window.setTimeout(
        complete,
        TRANSITION_FALLBACK_MS
      );
    }

    /**
     * Sets an explicit current height before starting a transition.
     */
    function freezeCurrentHeight() {
      panel.style.transition = 'none';
      panel.style.maxHeight =
        `${panel.getBoundingClientRect().height}px`;

      // Force the browser to apply the current height immediately.
      panel.offsetHeight;

      panel.style.transition = '';
    }

    /**
     * Expands the filter panel.
     */
    function expand() {
      clearTransitionHandler();
      if (isCompact()) {
        catalog.getForm()?.dispatchEvent(new CustomEvent('adocs:catalog-open'));
      }

      if (region) {
        region.classList.remove(REGION_HIDDEN_CLASS);
      }

      /*
       * The region may previously have used display: none.
       * Wait until the browser has put it back into the layout.
       */
      freezeCurrentHeight();
      const currentHeight = panel.style.maxHeight;
      // Measure normal flow, excluding the mobile panel's result overlay.
      panel.style.maxHeight = 'none';
      const targetHeight = panel.getBoundingClientRect().height;
      panel.style.maxHeight = currentHeight;
      panel.offsetHeight;
      setExpanded(toggle, true);
      panel.inert = false;

      animationFrame = requestAnimationFrame(() => {
        animationFrame = null;
        panel.style.maxHeight = `${targetHeight}px`;

        afterTransition(() => {
          if (isExpanded(toggle)) {
            /*
             * Remove the fixed height after opening so that dynamic form
             * contents can change the panel height naturally.
             */
            panel.style.maxHeight = 'none';
          }
        });

      });
    }

    /**
     * Collapses the filter panel.
     */
    function collapse() {
      clearTransitionHandler();
      freezeCurrentHeight();
      setExpanded(toggle, false);
      if (panel.contains(document.activeElement)) {
        toggle.focus();
      }
      panel.inert = true;
      panel.style.maxHeight = '0px';

      afterTransition(() => {
        if (!isExpanded(toggle) && region) {
          /*
           * Remove the complete region from the layout after the closing
           * animation so that it leaves no empty gap.
           */
          region.classList.add(REGION_HIDDEN_CLASS);
        }
      });

    }

    /**
     * Apply the current/return state immediately, before interaction animations.
     */
    function initializeState() {
      const saved = currentExpanded ?? Drupal.adocsCatalogReturn?.getInitialState()?.expanded
        ?? (catalog.settings().rememberPanelState ? catalog.readStored('expanded') : null);
      const expanded = typeof saved === 'boolean' ? saved
        : (getComputedStyle(controls).getPropertyValue('--catalog-filters-compact').trim() !== '1'
          || catalog.settings().mobileInitiallyExpanded);

      panel.style.maxHeight = expanded ? 'none' : '0px';
      panel.inert = !expanded;

      setExpanded(toggle, expanded);

      if (region) {
        region.classList.toggle(
          REGION_HIDDEN_CLASS,
          !expanded
        );
      }
    }

    initializeState();
    document.documentElement.classList.remove('catalog-return-collapsed');

    panel.addEventListener('adocs:catalog-select', () => {
      if (!isCompact()) return;
      action.focus({ preventScroll: true });
      collapse();
    }, listenerOptions);

    panel.addEventListener('click', (event) => {
      if (isCompact() && event.target instanceof Element
        && event.target.closest('.filter-reset')) {
        collapse();
        action.focus();
      }
    }, listenerOptions);

    toggle.addEventListener('click', () => {
      if (isExpanded(toggle)) {
        collapse();
      } else {
        expand();
      }
    }, listenerOptions);
    return () => {
      clearTransitionHandler();
      listeners.abort();
    };
  }

  Drupal.behaviors.filterToggle = {
    attach() {
      /*
       * Search the complete document intentionally:
       *
       * BigPipe may render the button and filter panel in separate passes.
       * A later behavior attachment whose context is only the filter region
       * would otherwise not contain the already-rendered button.
       */
      if (!Drupal.adocsCatalog.getState()) return;
      document
        .querySelectorAll('.filter-toggle')
        .forEach((toggle) => {
          const panelId = toggle.getAttribute('aria-controls');

          if (!panelId) {
            return;
          }

          const panel = document.getElementById(panelId);

          /*
           * Do not call once() until the panel exists.
           *
           * Otherwise BigPipe could mark the button as initialized before
           * its controlled panel has been rendered.
           */
          if (!panel) {
            return;
          }

          const previous = instances.get(toggle);
          if (previous && previous.panel !== panel) {
            previous.cleanup();
            once.remove('filter-toggle', [toggle]);
          }
          once('filter-toggle', [toggle]).forEach(
            (processedToggle) => {
              instances.set(processedToggle, {
                panel, cleanup: initializeFilterToggle(processedToggle, panel),
              });
            }
          );
        });
    },
    detach(context, settings, trigger) {
      if (trigger !== 'unload') return;
      const toggles = [...context.querySelectorAll('.filter-toggle')];
      if (context.matches?.('.filter-toggle')) toggles.push(context);
      once.remove('filter-toggle', toggles).forEach((toggle) => {
        instances.get(toggle)?.cleanup();
        instances.delete(toggle);
      });
    },
  };
})(Drupal, once);
